import { html, nothing } from "lit";
import type { SessionsListResult } from "../types.ts";
import {
  parseSessionKeyPath,
  sessionKeyFromPath,
  type SessionKeyPath,
} from "../chat/session-key-path.ts";
import { renderSessionPathNavigator } from "./session-path-navigator.ts";

export type LogsScopeBarProps = {
  viewAll: boolean;
  scopeLinked: boolean;
  scopePath: SessionKeyPath;
  fleetSessionKey: string;
  openTabs: string[];
  sessions: SessionsListResult | null;
  connected: boolean;
  serverFiltersSupported?: boolean;
  onToggleViewAll: (viewAll: boolean) => void;
  onToggleLinked: (linked: boolean) => void;
  onScopePathChange: (path: SessionKeyPath) => void;
};

export function renderLogsScopeBar(props: LogsScopeBarProps) {
  const activeSessionKey = sessionKeyFromPath(props.scopePath) ?? props.fleetSessionKey;

  return html`
    <div class="logs-scope-bar fleet-session-bar" role="region" aria-label="Log stream scope">
      <div class="row" style="justify-content: space-between; align-items: center; gap: 8px; flex-wrap: wrap;">
        <div class="chip-row" style="margin: 0;">
          <label class="chip log-chip ${props.viewAll ? "active" : ""}">
            <input
              type="radio"
              name="logs-view-mode"
              .checked=${props.viewAll}
              @change=${() => props.onToggleViewAll(true)}
            />
            <span>All gateway logs</span>
          </label>
          <label class="chip log-chip ${!props.viewAll ? "active" : ""}">
            <input
              type="radio"
              name="logs-view-mode"
              .checked=${!props.viewAll}
              @change=${() => props.onToggleViewAll(false)}
            />
            <span>Session stream</span>
          </label>
        </div>
        ${
          !props.viewAll
            ? html`
                <button
                  type="button"
                  class="btn btn--sm"
                  @click=${() => props.onToggleLinked(!props.scopeLinked)}
                >
                  ${props.scopeLinked ? "Unlink" : "Re-link to Fleet"}
                </button>
              `
            : nothing
        }
      </div>
      ${
        props.viewAll
          ? html`
              <p class="muted" style="margin: 8px 0 0; font-size: 12px">
                Tailing the full Jaisiu JSONL file (all subsystems). Use search and level filters below to narrow
                results.
              </p>
            `
          : html`
              <p class="muted" style="margin: 8px 0 0; font-size: 12px;">
                ${
                  props.scopeLinked
                    ? "Following Fleet session"
                    : "Custom log stream (unlinked from Fleet)"
                }
              </p>
            `
      }
      ${
        props.serverFiltersSupported === false
          ? html`
              <div class="callout" style="margin-top: 8px">
                Gateway build is older than this UI — filtering runs in the browser only. Restart the gateway
                after <span class="mono">pnpm build</span> for server-side filters.
              </div>
            `
          : nothing
      }
      ${
        props.viewAll
          ? nothing
          : html`
      <div style="margin-top: 10px;">
        ${renderSessionPathNavigator({
          activeSessionKey,
          activePath: props.scopePath,
          openTabs: props.openTabs,
          sessions: props.sessions,
          onSelectSession: (key) => {
            if (props.scopeLinked) {
              props.onToggleLinked(false);
            }
            props.onScopePathChange(parseSessionKeyPath(key));
          },
          showActiveFooter: false,
          ariaLabel: "Log stream scope",
          className: "fleet-session-path logs-scope-path",
        })}
      </div>
            `
      }
      ${
        !props.connected
          ? html`
              <div class="muted" style="margin-top: 8px; font-size: 12px">
                Connect to the gateway to discover session keys.
              </div>
            `
          : nothing
      }
    </div>
  `;
}
